import { useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import history from 'App/pages/routes/history';
import type { AgentId, Route } from './WorkspaceSidebar';

const BASE = '/ai-workspace';

const SECTIONS: ReadonlyArray<Route> = ['chat', 'routines', 'org', 'agents', 'settings', 'usage', 'history', 'inbox'];

export type WorkspaceNavState = {
	route: Route;
	agentRoute: AgentId | null;
	conversationId: number | null;
};

const DEFAULT_STATE: WorkspaceNavState = { route: 'chat', agentRoute: null, conversationId: null };

export const parsePath = (pathname: string): WorkspaceNavState => {
	const rest = pathname.startsWith(BASE) ? pathname.slice(BASE.length) : '';
	const segments = rest.split('/').filter(Boolean);
	const section = segments[0] as Route | undefined;
	if (!section || !SECTIONS.includes(section)) return DEFAULT_STATE;
	if (section === 'chat') {
		const id = Number(segments[1]);
		return { route: 'chat', agentRoute: null, conversationId: Number.isFinite(id) ? id : null };
	}
	if (section === 'agents') {
		const id = Number(segments[1]);
		return { route: 'agents', agentRoute: Number.isFinite(id) ? id : null, conversationId: null };
	}
	return { route: section, agentRoute: null, conversationId: null };
};

export const buildPath = (state: WorkspaceNavState): string => {
	if (state.route === 'chat')
		return state.conversationId != null ? `${BASE}/chat/${state.conversationId}` : `${BASE}/chat`;
	if (state.route === 'agents')
		return state.agentRoute != null ? `${BASE}/agents/${state.agentRoute}` : `${BASE}/agents`;
	return `${BASE}/${state.route}`;
};

const currentUrlState = (): WorkspaceNavState => parsePath(window.location.hash.replace(/^#/, '').split('?')[0]);

export type WorkspaceNav = WorkspaceNavState & {
	navigate: (patch: Partial<WorkspaceNavState>, opts?: { replace?: boolean }) => void;
	setConversationId: (id: number | null) => void;
};

export const useWorkspaceNav = (asPage: boolean): WorkspaceNav => {
	const location = useLocation();
	const [state, setState] = useState<WorkspaceNavState>(DEFAULT_STATE);

	const navigate = useCallback(
		(patch: Partial<WorkspaceNavState>, opts?: { replace?: boolean }) => {
			if (asPage) {
				const current = currentUrlState();
				const path = buildPath({ ...current, ...patch });
				if (opts?.replace || path === buildPath(current)) history.replace(path);
				else history.push(path);
			} else {
				setState(s => ({ ...s, ...patch }));
			}
		},
		[asPage]
	);

	const setConversationId = useCallback(
		(id: number | null) => navigate({ conversationId: id }, { replace: true }),
		[navigate]
	);

	const current = asPage ? parsePath(location.pathname) : state;
	return { ...current, navigate, setConversationId };
};
